import { Observable } from "rxjs"
import { AxiosResponse } from "axios"

import {
  QueryHistoryData,
  QueryHistoryFilters,
  SearchingTrendPointData,
  AppUsageSummaryData,
  APIUsageSummaryData,
  APICallsAmountPointData,
  APICallsHistoryData,
  APICallStatus,
  UserTrendPointData,
} from "@framework/types/analytics"
import {
  PaginationListMeta,
  PaginationParams,
  VirtualListChunk,
  VirtualListChunkMeta,
} from "@framework/types/utils"
import { DateRange } from "@framework/types/common"
import { ActiveUsers } from "@framework/types/user"
import { getTimeZoneString } from "@utils/date"
import { queryDocumentTypeToQueryTypeFilterName } from "@framework/types/workbook-report"

import HttpService from "./http.service"

export interface GetQuestionsStatisticResponse extends AppUsageSummaryData {}

export interface GetAPIUsageSummaryResponse extends APIUsageSummaryData {}

export interface GetAPIUsageTrendsResponse {
  data: APICallsAmountPointData[]
}

export type APICallsLogsFilter = {
  url?: string
  apiKeyName?: string
  startDate?: string
  endDate?: string
  status?: APICallStatus
}

export interface GetAPIUsageHistoryResponse {
  data: APICallsHistoryData[]
  meta: {
    total: number
    pageNum: string
    pageSize: string
  }
}

export interface GetActiveUserVirtualListResponse {
  data: ActiveUsers[]
  meta: VirtualListChunkMeta
}

export type GetLoadSearchingTrendReportResponse = SearchingTrendPointData[]

export type GetLoadUserTrendReportResponse = UserTrendPointData[]

export interface GetLoadQueryHistoryReportResponse {
  data?: QueryHistoryData[]
  meta: PaginationListMeta
}

class AnalyticsAPI extends HttpService {
  loadQuestionsStatistic = (
    params: {
      avatarIds?: string[]
      startDate: string // DD-MM-YYYY
      endDate: string // DD-MM-YYYY
      countries?: string[]
    },
    signal?: AbortSignal
  ): Promise<AxiosResponse<GetQuestionsStatisticResponse>> =>
    this.get(
      "ts/analytics/overview",
      true,
      {
        ...params,
        countries: params.countries?.length
          ? params.countries.join(",")
          : undefined,
      },
      null,
      signal
    )

  loadSearchingTrendReport = (
    params: {
      avatarIds?: string[]
      startDate?: string // DD-MM-YYYY
      endDate?: string // DD-MM-YYYY
      countries?: string[]
    },
    signal?: AbortSignal
  ): Promise<AxiosResponse<GetLoadSearchingTrendReportResponse>> =>
    this.get(
      "ts/analytics/query-trend",
      true,
      {
        ...params,
        countries: params.countries?.length
          ? params.countries.join(",")
          : undefined,
      },
      null,
      signal
    )

  loadUserTrendReport = (
    startDate?: string, // DD-MM-YYYY
    endDate?: string, // DD-MM-YYYY
    countries?: string[],
    signal?: AbortSignal
  ): Promise<AxiosResponse<GetLoadUserTrendReportResponse>> =>
    this.get(
      "ts/analytics/users",
      true,
      {
        startDate,
        endDate,
        countries: countries?.length ? countries.join(",") : undefined,
      },
      null,
      signal
    )

  loadQueryHistoryReport = (
    meta?: PaginationParams,
    filters?: QueryHistoryFilters,
    signal?: AbortSignal
  ): Promise<AxiosResponse<GetLoadQueryHistoryReportResponse>> =>
    this.get(
      "ts/v2/admin/questions/data",
      true,
      {
        ...meta,
        startDate: filters?.date?.start,
        endDate: filters?.date?.end,
        userIds: filters?.userIds,
        assignStatus: filters?.assignStatus,
        ratings: filters?.ratings,
        searchQuery: filters?.searchQuery,
        expertStatus: filters?.expertStatus,
        isUserHistoryFlow: filters?.isUserHistoryFlow,
        queryTypes: filters?.queryTypes?.map(
          queryDocumentTypeToQueryTypeFilterName
        ),
        countries: filters?.countries?.length
          ? filters.countries.join(",")
          : undefined,
      },
      null,
      signal
    )

  loadAPIUsageSummary$ = (): Observable<
    AxiosResponse<GetAPIUsageSummaryResponse>
  > => this.getStream$("ts/api-call-logs/overview")

  loadAPIUsageTrends$ = (
    period: DateRange
  ): Observable<AxiosResponse<GetAPIUsageTrendsResponse>> =>
    this.getStream$("ts/api-call-logs/usage-trends", {
      params: {
        startDate: period.start,
        endDate: period.end,
        timeZone: getTimeZoneString(),
      },
    })

  downloadAPIUsageTrendReport = (
    startDate?: string, // DD-MM-YYYY
    endDate?: string // DD-MM-YYYY
  ): Promise<AxiosResponse<Blob>> =>
    this.download("ts/api-call-logs/usage-trends/download", {
      startDate,
      endDate,
      timeZone: getTimeZoneString(),
    })

  loadAPIUsageHistory$ = (
    query: string,
    pagination: PaginationParams,
    filter: APICallsLogsFilter
  ): Observable<AxiosResponse<GetAPIUsageHistoryResponse>> =>
    this.getStream$("ts/api-call-logs/api-logs", {
      params: { ...filter, ...pagination, url: query },
    })

  downloadAPIUsageHistory = (
    query: string,
    filter: APICallsLogsFilter
  ): Promise<AxiosResponse<Blob>> =>
    this.download("ts/api-call-logs/api-logs/download", {
      ...filter,
      url: query,
      timeZone: getTimeZoneString(),
    })

  downloadUsageAnalyticsReport = (
    period: DateRange,
    countries?: string[]
  ): Promise<AxiosResponse<Blob>> =>
    this.download("ts/analytics/download", {
      startDate: period.start,
      endDate: period.end,
      timeZone: getTimeZoneString(),
      countries: countries?.length ? countries.join(",") : undefined,
    })

  loadActiveUsers$ = (
    filter: {
      period: DateRange
      countries?: string[]
    },
    meta: VirtualListChunk
  ): Observable<AxiosResponse<GetActiveUserVirtualListResponse>> =>
    this.getStream$(`ts/analytics/top-active-users`, {
      params: {
        startDate: filter.period.start,
        endDate: filter.period.end,
        countries: filter.countries?.length
          ? filter.countries.join(",")
          : undefined,
        ...meta,
      },
    })
}

export default new AnalyticsAPI()
